import { createServerFn } from "@tanstack/react-start";
import z from "zod";
import {
  mapsCallSourceSchema,
  type MapsCallSource,
} from "~/lib/maps/attribution";
import {
  readPlaceDetailsCacheFromDb,
  type CachedPlaceDetails,
  writePlaceDetailsCacheToDb,
} from "~/lib/server-functions/place-details-cache-db";
import { guardedGoogleFetch } from "~/lib/server-functions/maps-outbound-call";
import {
  hashMapsRequestKey,
  hashSessionScope,
  logMapsUsage,
} from "~/lib/server-functions/maps-usage-log";
import { guardMapsRequest } from "./maps-request-guard";
import { consumePlacesAutocompleteSession } from "./maps-places-session";

export type GeoBoundsBox = {
  northeast: { lat: number; lng: number };
  southwest: { lat: number; lng: number };
};

export type PlaceDetails = {
  city: string;
  state: string;
  street: string;
  houseNumber: string;
  country: string;
  postalCode: string;
  lat: number;
  lng: number;
  /** Geographic extent from Place Details (preferred for countries/regions). */
  viewport?: GeoBoundsBox;
  bounds?: GeoBoundsBox;
  hasCity?: boolean;
  hasState?: boolean;
  hasCountry?: boolean;
};

const placeDetailsMemoryCache = new Map<
  string,
  { entry: CachedPlaceDetails; timestamp: number }
>();

const PLACE_DETAILS_MEMORY_TTL_MS = 10 * 60 * 1000;
const DB_PLACE_DETAILS_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const NEGATIVE_PLACE_DETAILS_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

/** Bounds the L1 map so a long-lived worker cannot grow it without limit. */
const PLACE_DETAILS_MEMORY_MAX_ENTRIES = 1_000;

function rememberPlaceDetailsInMemory(
  requestKeyHash: string,
  entry: CachedPlaceDetails,
): void {
  // Refresh recency so eviction is LRU rather than insertion-ordered.
  placeDetailsMemoryCache.delete(requestKeyHash);
  placeDetailsMemoryCache.set(requestKeyHash, { entry, timestamp: Date.now() });
  while (placeDetailsMemoryCache.size > PLACE_DETAILS_MEMORY_MAX_ENTRIES) {
    const oldestKey = placeDetailsMemoryCache.keys().next().value as
      | string
      | undefined;
    if (!oldestKey) break;
    placeDetailsMemoryCache.delete(oldestKey);
  }
}

function parseLatLngBox(
  box:
    | {
        northeast?: { lat?: number; lng?: number };
        southwest?: { lat?: number; lng?: number };
      }
    | undefined,
): GeoBoundsBox | undefined {
  const neLat = box?.northeast?.lat;
  const neLng = box?.northeast?.lng;
  const swLat = box?.southwest?.lat;
  const swLng = box?.southwest?.lng;
  if (
    typeof neLat !== "number" ||
    !Number.isFinite(neLat) ||
    typeof neLng !== "number" ||
    !Number.isFinite(neLng) ||
    typeof swLat !== "number" ||
    !Number.isFinite(swLat) ||
    typeof swLng !== "number" ||
    !Number.isFinite(swLng)
  ) {
    return undefined;
  }
  return {
    northeast: { lat: neLat, lng: neLng },
    southwest: { lat: swLat, lng: swLng },
  };
}

export function parsePlaceDetailsFromGoogleOk(data: {
  addressComponents?: Array<{ types?: string[]; longText?: string }>;
  location?: { latitude?: number; longitude?: number };
  viewport?: {
    high?: { latitude?: number; longitude?: number };
    low?: { latitude?: number; longitude?: number };
  };
}): PlaceDetails {
  const components = data.addressComponents ?? [];
  const get = (type: string) =>
    components.find((c) => c.types?.includes(type))?.longText ?? null;

  const hasCity = components.some(
    (c) =>
      c.types?.includes("locality") ||
      c.types?.includes("postal_town") ||
      c.types?.includes("sublocality"),
  );
  const hasState = components.some(
    (c) =>
      c.types?.includes("administrative_area_level_1") ||
      c.types?.includes("administrative_area_level_2"),
  );
  const hasCountry = components.some((c) => c.types?.includes("country"));

  const lat = data.location?.latitude;
  const lng = data.location?.longitude;
  if (
    typeof lat !== "number" ||
    !Number.isFinite(lat) ||
    typeof lng !== "number" ||
    !Number.isFinite(lng)
  ) {
    throw new Error("Places API New response omitted a valid location");
  }
  const viewport = parseLatLngBox(
    data.viewport
      ? {
          northeast: {
            lat: data.viewport.high?.latitude,
            lng: data.viewport.high?.longitude,
          },
          southwest: {
            lat: data.viewport.low?.latitude,
            lng: data.viewport.low?.longitude,
          },
        }
      : undefined,
  );

  return {
    city: get("locality") || get("postal_town") || "",
    state: get("administrative_area_level_1") || "",
    street: get("route") || "",
    houseNumber: get("street_number") || "",
    country: get("country") || "",
    postalCode: get("postal_code") || "",
    lat,
    lng,
    viewport,
    hasCity,
    hasState,
    hasCountry,
  };
}

export const getPlaceDetails = createServerFn({ method: "POST" })
  .inputValidator(
    z.object({
      placeId: z.string().trim().min(1).max(512),
      sessionToken: z.string().trim().max(128).optional(),
      /** Callsite for cost attribution — a finite enum, never free text. */
      source: mapsCallSourceSchema,
    }),
  )
  .handler(async (ctx): Promise<PlaceDetails | null> => {
    const apiKey = process.env.GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      throw new Error("Google Maps API key not configured (GOOGLE_MAPS_API_KEY)");
    }

    const source: MapsCallSource = ctx.data.source;
    const placeId = ctx.data.placeId.trim();
    if (!placeId) {
      return null;
    }

    // When a session token is present we MUST call Google so Autocomplete is
    // billed per session. Skipping cache here is intentional — only pass a
    // sessionToken from low-traffic flows that actually need Place Details
    // (e.g. LocationSearchInput). Never use this for SearchBar / CMS loc=.
    const requestedSessionToken = ctx.data.sessionToken?.trim() || undefined;
    if (requestedSessionToken) {
      const sessionRequestGuard = await guardMapsRequest(
        "place_details",
        source,
      );
      if (!sessionRequestGuard.allowed) return null;
    }
    const sessionToken =
      requestedSessionToken &&
      (await consumePlacesAutocompleteSession(requestedSessionToken))
        ? requestedSessionToken
        : undefined;
    if (requestedSessionToken && !sessionToken) {
      await logMapsUsage({
        api: "place_details",
        source,
        stage: "blocked_invalid_session",
        outcome: "blocked_invalid_session",
        cacheLayer: "none",
        googleStatus: "INVALID_OR_CONSUMED_SESSION",
        durationMs: 0,
        hadSessionToken: true,
      });
    }
    const hadSessionToken = Boolean(sessionToken);
    const query = new URLSearchParams({ languageCode: "de" });
    if (sessionToken) query.set("sessionToken", sessionToken);
    const url = `https://places.googleapis.com/v1/places/${encodeURIComponent(placeId)}?${query.toString()}`;

    // v3: Places API New response, kept separate from legacy cache rows.
    const requestKeyHash = hashMapsRequestKey(
      `${placeId}|place_details|v3|new|de`,
    );

    let cachedFallback: PlaceDetails | null | undefined;

    if (!sessionToken) {
      const memoryLookupStarted = Date.now();
      const mem = placeDetailsMemoryCache.get(requestKeyHash);
      if (mem) {
        const age = Date.now() - mem.timestamp;
        const ttlMs =
          mem.entry.kind === "zero"
            ? NEGATIVE_PLACE_DETAILS_CACHE_TTL_MS
            : PLACE_DETAILS_MEMORY_TTL_MS;
        if (age < ttlMs) {
          const lookupDurationMs = Date.now() - memoryLookupStarted;
          void logMapsUsage({
            api: "place_details",
            source,
            stage: "cache_hit",
            cacheLayer: "memory",
            outcome:
              mem.entry.kind === "zero"
                ? "cache_hit_memory_zero_results"
                : "cache_hit_memory",
            durationMs: Math.max(0, lookupDurationMs),
            requestKeyHash,
            hadSessionToken,
          });
          return mem.entry.kind === "ok" ? mem.entry.value : null;
        }
        placeDetailsMemoryCache.delete(requestKeyHash);
      }

      const fromDb = await readPlaceDetailsCacheFromDb(requestKeyHash);
      if (fromDb) {
        void logMapsUsage({
          api: "place_details",
          source,
          stage: "cache_hit",
          cacheLayer: "database",
          outcome:
            fromDb.kind === "zero"
              ? "cache_hit_db_zero_results"
              : "cache_hit_db",
          durationMs: 0,
          requestKeyHash,
          hadSessionToken,
        });
        rememberPlaceDetailsInMemory(requestKeyHash, fromDb);
        return fromDb.kind === "ok" ? fromDb.value : null;
      }
    } else {
      // Prefer returning a warm cache value if the session-closing Google call fails.
      const mem = placeDetailsMemoryCache.get(requestKeyHash);
      if (mem) {
        const age = Date.now() - mem.timestamp;
        const ttlMs =
          mem.entry.kind === "zero"
            ? NEGATIVE_PLACE_DETAILS_CACHE_TTL_MS
            : PLACE_DETAILS_MEMORY_TTL_MS;
        if (age < ttlMs) {
          cachedFallback = mem.entry.kind === "ok" ? mem.entry.value : null;
        }
      }
      if (cachedFallback === undefined) {
        const fromDb = await readPlaceDetailsCacheFromDb(requestKeyHash);
        if (fromDb) {
          cachedFallback = fromDb.kind === "ok" ? fromDb.value : null;
          rememberPlaceDetailsInMemory(requestKeyHash, fromDb);
        }
      }
    }

    const requestGuard = requestedSessionToken
      ? ({ allowed: true } as const)
      : await guardMapsRequest("place_details", source);
    if (!requestGuard.allowed) return cachedFallback ?? null;

    void logMapsUsage({
      api: "place_details",
      source,
      stage: "cache_miss",
      cacheLayer: "none",
      outcome: "cache_miss",
      durationMs: 0,
      requestKeyHash,
      hadSessionToken,
    });

    const timeoutMsRaw = Number(process.env.GOOGLE_MAPS_FETCH_TIMEOUT_MS);
    const timeoutMs =
      Number.isFinite(timeoutMsRaw) && timeoutMsRaw >= 1000 && timeoutMsRaw <= 60_000
        ? timeoutMsRaw
        : 12_000;

    const guarded = await guardedGoogleFetch<PlaceDetails | null>({
      api: "place_details",
      source,
      requestKeyHash,
      // Requests carrying different billing sessions must not coalesce.
      sessionScope: sessionToken ? hashSessionScope(sessionToken) : null,
      hadSessionToken,
      recheckAfterLock: sessionToken
        ? undefined
        : async () => {
            const cached = await readPlaceDetailsCacheFromDb(requestKeyHash);
            if (!cached) return { hit: false as const };
            rememberPlaceDetailsInMemory(requestKeyHash, cached);
            return {
              hit: true as const,
              value: cached.kind === "ok" ? cached.value : null,
            };
          },
      fetchFromGoogle: async (): Promise<PlaceDetails | null> => {
        const started = Date.now();
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
          let res: Response;
          try {
            res = await fetch(url, {
              signal: controller.signal,
              headers: {
                "X-Goog-Api-Key": apiKey,
                "X-Goog-FieldMask":
                  "addressComponents,location,viewport",
              },
            });
          } finally {
            clearTimeout(timeoutId);
          }
          const data = await res.json();
          const durationMs = Date.now() - started;

          if (!res.ok) {
            const isZeroResults = res.status === 404;
            await logMapsUsage({
              api: "place_details",
              source,
              stage: "google_result",
              outcome: isZeroResults ? "zero_results" : "error",
              googleStatus:
                typeof data.error?.status === "string"
                  ? data.error.status
                  : `HTTP_${res.status}`,
              httpStatus: res.status,
              durationMs,
              requestKeyHash,
              hadSessionToken,
            });
            if (isZeroResults) {
              // Only a definitive "no such place" is cached; transient Google
              // failures fall through uncached.
              rememberPlaceDetailsInMemory(requestKeyHash, { kind: "zero" });
              await writePlaceDetailsCacheToDb(
                requestKeyHash,
                { kind: "zero" },
                new Date(Date.now() + NEGATIVE_PLACE_DETAILS_CACHE_TTL_MS),
              );
              return null;
            }
            // Session-close call failed after Autocomplete: still return warm cache.
            if (cachedFallback !== undefined) {
              return cachedFallback;
            }
            return null;
          }

          await logMapsUsage({
            api: "place_details",
            source,
            stage: "google_result",
            outcome: "success",
            googleStatus: "OK",
            httpStatus: res.status,
            durationMs,
            requestKeyHash,
            hadSessionToken,
          });

          const details = parsePlaceDetailsFromGoogleOk(data);

          rememberPlaceDetailsInMemory(requestKeyHash, {
            kind: "ok",
            value: details,
          });
          await writePlaceDetailsCacheToDb(
            requestKeyHash,
            { kind: "ok", value: details },
            new Date(Date.now() + DB_PLACE_DETAILS_CACHE_TTL_MS),
          );

          return details;
        } catch (err) {
          await logMapsUsage({
            api: "place_details",
            source,
            stage: "error",
            outcome: "error",
            googleStatus: null,
            durationMs: Date.now() - started,
            requestKeyHash,
            hadSessionToken,
          });
          if (cachedFallback !== undefined) {
            return cachedFallback;
          }
          throw err;
        }
      },
    });

    if (guarded.status === "blocked") {
      // Prefer a still-valid cached value over failing the caller outright.
      return cachedFallback ?? null;
    }

    return guarded.value;
  });
